import { TaxonomyTermStatus } from '@edgebox/sync-core-data-definitions';
import { MUTED_TERM_CLASS_NAME, termStatusPresentation, TermStatusPresentation } from './termStatusPresentation';

/**
 * The ONE definition of how a cited website is grouped, named and ranked.
 *
 * Three surfaces show the same sources — the answers list chips, the answer detail's "Sources
 * cited" block, and the Analytics "Sources" module — and the same source must never read
 * differently between the list chips and the Analytics module. Each surface asks this module;
 * none of them folds, names or ranks a host itself.
 *
 * Everything here is React-free and client-free so it can be unit tested on its own.
 */

/** How sources are grouped on a surface. */
export type SourceGrouping =
  /** By registrable domain — `drupal.org` covers `www.drupal.org` and `new.drupal.org`. */
  | 'domain'
  /** By the full hostname exactly as cited (with a leading `www.` folded away). */
  | 'host';

/**
 * Grouping defaults to the registrable domain.
 *
 * A marketer thinks "does the AI cite Drupal.org?", not "does it cite new.drupal.org?" — splitting
 * one publisher across its subdomains buries the answer. The full-hostname view stays one checkbox
 * away for anyone who needs the precision.
 */
export const DEFAULT_SOURCE_GROUPING: SourceGrouping = 'domain';

/** Label for the control that switches between the two groupings. */
export const SOURCE_GROUPING_TOGGLE_LABEL = 'Show full hostnames';

/** Why that control is there, in the reader's terms. */
export const SOURCE_GROUPING_TOGGLE_HINT =
  'Sources are grouped by website by default, so www.example.com and docs.example.com count as one. Tick this to list every hostname separately.';

/**
 * Folds one hostname to the form the product treats as canonical.
 *
 * Lowercased, trailing dot removed, and a leading `www.` dropped — the single mechanical alias.
 * Sync Core deliberately stores the host verbatim so the mirror records what was actually cited;
 * the fold is a read-time decision, and this is where it is made.
 */
export function foldHost(value?: string | null): string {
  const host = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\.+$/, '');

  if (!host) {
    return '';
  }

  return host.startsWith('www.') ? host.slice(4) : host;
}

/**
 * One row of the run's `citationHosts` mirror: one (host, entity, evidence tier) triple and how
 * often it occurred in this answer.
 *
 * Typed locally so both twins of the published interface fit it: the runtime row carries `entity` as
 * a runtime revision reference and the serialized row as a serialized one, and this module reads a
 * key off either. `domain` is no longer the reason — since scdd 1.296.0 the published type declares
 * it, and `CitationHostRowLike` is a supertype of the published row rather than a stand-in for it,
 * so `run.citationHosts` now assigns to it with no cast.
 */
export interface CitationHostRowLike {
  host: string;
  /**
   * The registrable domain of `host`, derived server-side from the Public Suffix List.
   *
   * Still optional, and that is a fact about the DATA rather than about the client version: rows
   * mirrored before the field existed carry no domain until the re-materialization pass stamps
   * them. Absence means "not yet derived" — fall back to `host`, never read it as "no domain".
   */
  domain?: string | null;
  /** The taxonomy term this citation was credited to. Absent = an independent source. */
  entity?: unknown;
  count?: number | null;
}

/** Reads a stable term key off a runtime/serialized revision reference (or a bare string). */
export function sourceEntityTermKey(reference: unknown): string {
  if (typeof reference === 'string') {
    return reference.trim();
  }
  if (!reference || typeof reference !== 'object') {
    return '';
  }
  const record = reference as { getStableKey?: () => string; key?: unknown };
  return String(record.getStableKey?.() || record.key || '').trim();
}

/**
 * The key one mirror row groups under.
 *
 * In `domain` mode the server-derived registrable domain wins; when it is absent the row falls back
 * to its folded host. That fallback is deliberately conservative: deriving a registrable domain in
 * the browser would need a second Public Suffix List, and a second list is a second answer — the
 * `co.uk` family alone is enough to make two implementations disagree and merge two publishers into
 * one group. Falling back to the host can only ever split a group that the server would have
 * merged, which is visible and harmless; guessing could merge groups that are not the same site.
 */
export function sourceGroupKey(row: CitationHostRowLike, grouping: SourceGrouping): string {
  if (grouping === 'domain') {
    const domain = foldHost(row.domain);
    if (domain) {
      return domain;
    }
  }

  return foldHost(row.host);
}

/** One entity a source was cited for, with how many of the group's citations it accounts for. */
export interface SourceEntityTally {
  termKey: string;
  citations: number;
}

/** Every citation of one source within one answer, already folded and tallied. */
export interface CitationSourceGroup {
  /** The folded domain or hostname this group is keyed by — real data, safe to render. */
  key: string;
  /** Total occurrences across every row in the group, attributed and independent alike. */
  citations: number;
  /** The tracked entities this source was cited for, strongest first. Empty = independent only. */
  entities: SourceEntityTally[];
  /** The distinct folded hostnames folded into this group, strongest first. */
  hosts: string[];
}

/**
 * Rolls a run's `citationHosts` mirror up into one group per source.
 *
 * The mirror carries one row per (host, entity, evidence tier), so a host cited for two brands is
 * two rows; the per-host total is the sum. Rows with no entity are counted into the total but
 * produce no entity tally — that absence is what makes a source independent, and inventing a
 * "not attributed" entity chip would turn meaningful data into noise.
 */
export function groupCitationHosts(
  rows: readonly CitationHostRowLike[] | null | undefined,
  grouping: SourceGrouping
): CitationSourceGroup[] {
  const groups = new Map<string, { key: string; citations: number; entities: Map<string, number>; hosts: Map<string, number> }>();

  for (const row of rows || []) {
    const key = sourceGroupKey(row, grouping);
    if (!key) {
      continue;
    }

    const count = typeof row.count === 'number' && Number.isFinite(row.count) && row.count > 0 ? row.count : 0;
    const group = groups.get(key) ?? { key, citations: 0, entities: new Map<string, number>(), hosts: new Map<string, number>() };
    group.citations += count;

    const host = foldHost(row.host);
    if (host) {
      group.hosts.set(host, (group.hosts.get(host) || 0) + count);
    }

    const termKey = sourceEntityTermKey(row.entity);
    if (termKey) {
      group.entities.set(termKey, (group.entities.get(termKey) || 0) + count);
    }

    groups.set(key, group);
  }

  return Array.from(groups.values())
    .map((group) => ({
      key: group.key,
      citations: group.citations,
      entities: Array.from(group.entities.entries())
        .map(([termKey, citations]) => ({ termKey, citations }))
        .sort((left, right) => right.citations - left.citations || left.termKey.localeCompare(right.termKey)),
      hosts: Array.from(group.hosts.entries())
        .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
        .map(([host]) => host),
    }))
    .sort((left, right) => right.citations - left.citations || left.key.localeCompare(right.key));
}

/** The fields this module needs off an `authority-source` taxonomy term. */
export interface AuthoritySourceTermLike {
  key?: unknown;
  name?: unknown;
  status?: TaxonomyTermStatus;
  aliases?: readonly (string | null | undefined)[] | null;
  /**
   * The hostnames the term declares as its own.
   *
   * An `authority-source` term is the one facet whose creation form makes a domain MANDATORY, so a
   * hand-curated source commonly reads "Example Docs" with `docs.example.com` in this field. Only
   * matching on the name would leave every properly curated source unmatched — the row would show
   * the hostname while the taxonomy already had a name for it.
   */
  thirdParty?: { domain?: string | null; domains?: readonly (string | null | undefined)[] | null } | null;
}

/** One curated source: the term behind a hostname, ready to render. */
export interface CuratedAuthoritySource {
  termKey: string;
  /** The term's display name — never its key. */
  name: string;
  status?: TaxonomyTermStatus;
}

/** Folded hostname -> the curated term that claims it, by name or by alias. */
export interface AuthoritySourceIndex {
  byFoldedHost: Map<string, CuratedAuthoritySource>;
}

/** An index that matches nothing — what every surface uses before the terms have loaded. */
export const EMPTY_AUTHORITY_SOURCE_INDEX: AuthoritySourceIndex = { byFoldedHost: new Map() };

/**
 * Indexes the `authority-source` terms by every hostname they answer to.
 *
 * Both sides are folded through `foldHost`, so an operator who typed `www.Example.com` as an alias
 * still matches the `example.com` the mirror recorded.
 *
 * Claims are made in three full passes — declared domains, then names, then aliases — and the first
 * claim on a hostname wins. Passing over the whole list once per strength is what makes the result
 * independent of the order the terms happen to arrive in; a single pass would let a weaker claim
 * from an earlier term beat a stronger one from a later term.
 */
export function buildAuthoritySourceIndex(terms: readonly AuthoritySourceTermLike[] | null | undefined): AuthoritySourceIndex {
  const byFoldedHost = new Map<string, CuratedAuthoritySource>();

  /** Registers a claim unless the host is already claimed. First claim of a pass wins. */
  const claim = (host: string, source: CuratedAuthoritySource) => {
    if (host && !byFoldedHost.has(host)) {
      byFoldedHost.set(host, source);
    }
  };

  const curated = (term: AuthoritySourceTermLike): CuratedAuthoritySource | undefined => {
    const termKey = String(term?.key ?? '').trim();
    const name = String(term?.name ?? '').trim();
    return termKey && name ? { termKey, name, status: term?.status } : undefined;
  };

  // Strongest first: a hostname the term explicitly declares as its own.
  for (const term of terms || []) {
    const source = curated(term);
    if (!source) {
      continue;
    }
    claim(foldHost(term?.thirdParty?.domain), source);
    for (const domain of term?.thirdParty?.domains || []) {
      claim(foldHost(domain), source);
    }
  }

  // Then the term's own name, which for a suggested source IS the hostname it was observed as.
  for (const term of terms || []) {
    const source = curated(term);
    if (source) {
      claim(foldHost(source.name), source);
    }
  }

  // Aliases last: the operator's catch-all for spellings the other two fields do not cover.
  for (const term of terms || []) {
    const source = curated(term);
    if (!source) {
      continue;
    }
    for (const alias of term?.aliases || []) {
      claim(foldHost(alias), source);
    }
  }

  return { byFoldedHost };
}

/** How one source should be labelled and styled, wherever it is rendered. */
export interface ResolvedSourceLabel {
  /** The folded domain or hostname. Always real data — never a taxonomy key. */
  key: string;
  /** What to render: the curated term's display name, or the hostname itself. */
  label: string;
  /** The curated term behind this source, when one claims it. */
  termKey?: string;
  /** True when a curated `authority-source` term claims this host by name or alias. */
  isCurated: boolean;
  /** The curated term's status, for the shared status→presentation helper. */
  status?: TaxonomyTermStatus;
  /** Presentation to spread onto the rendered element. Never `hidden` — see below. */
  presentation: TermStatusPresentation;
  /** True when the source is not curated yet — the "+ Add as source" row style. */
  isUncurated: boolean;
}

/**
 * Resolves how one grouped source reads.
 *
 * Curation NEVER gates the row: an unmatched hostname is returned as itself, muted, rather than
 * dropped — the measurement happened whether or not anybody curated the source.
 *
 * That is also why an archived term cannot hide a source. Archived terms are excluded from the
 * index at the fetch, so a source whose term was retired simply stops carrying the curated name
 * and falls back to its hostname. Hiding the row instead would delete a measurement.
 */
export function resolveSourceLabel(key: string, index?: AuthoritySourceIndex): ResolvedSourceLabel {
  const folded = foldHost(key);
  const curated = index?.byFoldedHost.get(folded);

  if (!curated) {
    return {
      key: folded,
      label: folded,
      isCurated: false,
      isUncurated: true,
      // No term at all. Dimmed with the sources ratio, and explained with the wording for "nobody
      // has named this site yet" — which is a different state from a suggestion awaiting review.
      presentation: {
        hidden: false,
        muted: true,
        showSuggestionIcon: false,
        className: MUTED_SOURCE_CLASS_NAME,
        title: UNCURATED_SOURCE_HINT,
      },
    };
  }

  const presentation = termStatusPresentation(curated.status, 'table-row');

  return {
    key: folded,
    label: curated.name,
    termKey: curated.termKey,
    isCurated: true,
    isUncurated: false,
    status: curated.status,
    // A hidden (archived) term cannot reach here — the index is built from non-archived terms —
    // but the flag is normalised anyway so no caller has to decide what hiding a measurement means.
    //
    // A suggested source says so in the sources vocabulary rather than the generic term wording: a
    // reader meeting a lightbulb chip beside a plain dimmed one was told the same sentence about
    // both, and could not tell what the lightbulb meant. The two states are now worded apart.
    presentation: {
      ...presentation,
      hidden: false,
      ...(presentation.muted ? { className: MUTED_SOURCE_CLASS_NAME, title: SUGGESTED_SOURCE_HINT } : {}),
    },
  };
}

/**
 * How strongly a de-emphasised source is dimmed.
 *
 * Two thirds rather than the half used for taxonomy term rows. A hostname is the only label these
 * chips have, and at 50% the domain became genuinely hard to read; a term row always has the rest
 * of its row to carry it. The term-status ratio is a separate, approved ruling and is untouched —
 * hence a constant of its own rather than a change to `MUTED_TERM_CLASS_NAME`.
 */
export const MUTED_SOURCE_CLASS_NAME = 'opacity-[0.67]';

/** Why an uncurated source is dimmed: nobody has named this site yet. */
export const UNCURATED_SOURCE_HINT = 'Not one of your tracked sources yet.';

/**
 * Why a suggested source is dimmed: a term for it EXISTS and is waiting on a decision.
 *
 * Deliberately different from the sentence above. Both states dim the chip, so the hover text is
 * the only thing that can tell them apart — and "what is the difference?" was exactly the question
 * the identical wording provoked.
 */
export const SUGGESTED_SOURCE_HINT = 'Suggested source — review it to start tracking it.';

/** The affordance that promotes an uncurated hostname into the taxonomy. */
export const ADD_AS_SOURCE_LABEL = '+ Add as source';

/** What clicking that affordance does. */
export const ADD_AS_SOURCE_HINT = 'Add this website to your tracked sources so it gets a name of its own.';

/** Up to how many source chips one answers-list row shows before collapsing the rest into "+N". */
export const SOURCE_CHIP_LIMIT = 3;

export interface SourceChipSelection<T> {
  /** The chips to render, in order. The filter subject leads when one is active. */
  chips: T[];
  /** How many sources are not shown — 0 when everything fits. */
  remaining: number;
}

/**
 * Picks which sources a compact row shows.
 *
 * Ranked by citations, capped at `limit` — except that an active filter subject is ALWAYS shown,
 * first, however weakly it was cited. That is the same rule the mentions column applies to "You":
 * the thing the reader filtered by must never be the thing the row hides, or the row looks like it
 * does not match the filter that produced it.
 */
export function selectSourceChips<T>(
  groups: readonly T[],
  options?: { limit?: number; isActive?: (group: T) => boolean }
): SourceChipSelection<T> {
  const limit = Math.max(0, options?.limit ?? SOURCE_CHIP_LIMIT);
  const isActive = options?.isActive;

  const activeIndex = isActive ? groups.findIndex((group) => isActive(group)) : -1;

  if (activeIndex < 0) {
    const chips = groups.slice(0, limit);
    return { chips, remaining: Math.max(0, groups.length - chips.length) };
  }

  const active = groups[activeIndex];
  const rest = groups.filter((_, index) => index !== activeIndex);
  const chips = [active, ...rest.slice(0, Math.max(0, limit - 1))];
  return { chips, remaining: Math.max(0, groups.length - chips.length) };
}
